import * as React from 'react';
import classNames from 'classnames';
import { isNil } from 'ramda';
import { IRailwaySuggest, IStations } from '../../../store/stations';
import { isArrayWithItems } from '../../../utils';
import { trackRailwaySearchKeypress, trackRailwaySuggestChosen } from '../tracking';
import { getSuggestLinkText } from '../../filters/geo_textfield/getSuggestLinkText';

import * as styles from './StationsSearch.css';
import { DropdownPopover, Input, Outside, IconSearch16, IconSuccess16, Tooltip } from '@cian/ui-kit';
import { TOOLTIP_NO_OFFER_TEXT } from '../../../constants';

interface IStationsSearchProps {
  stations: IStations;

  onSelectSuggest(id: number): void;
  onInputChange(value: string, trackNotFound?: boolean): void;
}

interface IStationsSearchState {
  isLoading: boolean;
  isResultsVisible: boolean;
  query: string;
}

export class StationsSearch extends React.Component<IStationsSearchProps, IStationsSearchState> {
  public state = {
    isLoading: false,
    isResultsVisible: false,
    query: '',
  };

  // Флажок для отправки аналитики только при первом нажатии на клавишу ввода поискового запроса
  private shouldSendAnalytics = true;

  public render() {
    return (
      <Outside onOutside={this.handleBlur}>
        <div className={styles['container']}>
          <DropdownPopover
            open={this.state.isResultsVisible && !!this.query}
            content={<div className={styles['list-container']}>{this.renderSuggestions()}</div>}
          >
            <Input
              leftAdornment={
                <div className={styles['search-icon']}>
                  <IconSearch16 display="block" color="primary_100" />
                </div>
              }
              placeholder="Название станции"
              value={this.state.query}
              onChange={this.handleInputChange}
              onFocus={this.handleFocus}
            />
          </DropdownPopover>
        </div>
      </Outside>
    );
  }
  private handleInputChange = (_e: React.ChangeEvent<HTMLInputElement>, query: string) => {
    if (this.shouldSendAnalytics) {
      trackRailwaySearchKeypress();

      // Отправляем только в первый раз
      this.shouldSendAnalytics = false;
    }

    this.setState({ query }, () => {
      if (this.query) {
        this.props.onInputChange(this.query, true);
      }
    });
  };

  private get query() {
    const result = this.state.query.trim();

    return result.length >= 2 ? result : '';
  }

  private renderSuggestions = () => {
    const {
      stations: { suggests = [] },
    } = this.props;
    if (!isArrayWithItems(suggests)) {
      return this.renderNothingFound();
    }

    return (
      <ul role="listbox" className={styles['list']}>
        {suggests.map(suggest => {
          const itemContent = (
            <div>
              {suggest.isActive && (
                <div className={styles['list-item_icon']}>
                  <IconSuccess16 color="primary_100" />
                </div>
              )}
              <span>{getSuggestLinkText(suggest.name, this.query)}</span>
              <div className={styles['list-item_label']}>{this.getDirectionName(suggest)}</div>
            </div>
          );

          return (
            <li
              role="option"
              key={`suggestion-${suggest.id}`}
              className={classNames(styles['list-item'], { [styles['list-item--disabled']]: !suggest.isEnabled })}
              onClick={this.handleSelectSuggest(suggest)}
            >
              {suggest.isEnabled ? (
                itemContent
              ) : (
                <Tooltip title={TOOLTIP_NO_OFFER_TEXT} placement="right">
                  {itemContent}
                </Tooltip>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  private renderNothingFound = () => (
    <div className={styles['empty']}>
      <div className={styles['empty-title']}>Ничего не найдено</div>
      <span>Укажите другую станцию</span>
    </div>
  );

  private getDirectionName = (suggest: IRailwaySuggest) => {
    const {
      stations: { directions },
    } = this.props;
    const selectedDirectionNames = directions.reduce((result: string[], { id, name }) => {
      if (isNil(id) || !name) {
        return result;
      }

      return suggest.directions.includes(id) ? result.concat(name) : result;
    }, []);

    const directionLabel = selectedDirectionNames.length > 1 ? 'Направления' : 'Направление';

    return `${directionLabel} ${selectedDirectionNames.join(', ')}`;
  };

  private handleSelectSuggest =
    ({ id, isEnabled }: IRailwaySuggest) =>
    () => {
      if (isEnabled) {
        trackRailwaySuggestChosen();
        this.setState(
          {
            isResultsVisible: false,
            query: '',
          },
          () => this.props.onSelectSuggest(id),
        );
      }
    };

  private handleBlur = () => this.setState({ isResultsVisible: false });

  private handleFocus = () => {
    this.setState({ isResultsVisible: true });
    this.props.onInputChange(this.query);
  };
}
